import { isEmpty, isNil, uniqBy } from 'lodash'
import { computed, toValue } from 'vue'

import { WORKSPACE_ENTITY_KEYS } from '@/utils/entity-keys'
import {
  AI_REPORT_ELEMENT_KEYS,
  AI_REPORT_INTERVAL_FILTER_LABEL_KEYS,
  AI_REPORT_KEYS,
  AI_REPORT_PAIR_KEYS,
  AI_REPORT_PAIRS_LIMIT,
  AI_REPORT_SECTION_KEYS,
  AI_REPORT_WEAKEST_PAIRS_COUNT
} from '@/utils/home-page/ai-report'
import { HOME_PAGE_INTERVAL_FILTER_VALUES, HOMEPAGE_GRADE_TYPES_OPTIONS } from '@/utils/homepage'

const { ALL_CURRENT, CUSTOM_RANGE } = HOME_PAGE_INTERVAL_FILTER_VALUES

const ALL_GRADE_TYPES = HOMEPAGE_GRADE_TYPES_OPTIONS.map(option => option.value)

const includesAll = ({ selectedValues = [], allValues = [] } = {}) => {
  return allValues.every(value => selectedValues.includes(value))
}

/*
 * Turns the Home filter state into the query of GET /homepage/aiReport and tells
 * whether a Home filter narrows a kept report, for the notes of FR-3.13 and FR-3.16.
 */
export const useAiReportFilters = ({
  selectedUserOrGroup = null,
  userSelected = false,
  workspaceIds = [],
  allWorkspaceIds = [],
  intervalTypeId = ALL_CURRENT,
  selectedIntervalIds = [],
  gradeTypes = []
} = {}) => {
  const selection = computed(() => {
    if (toValue(userSelected)) {
      return { groupId: null, accountId: toValue(selectedUserOrGroup) }
    }

    return { groupId: toValue(selectedUserOrGroup), accountId: null }
  })

  const isCustomRangeSelected = computed(() => toValue(intervalTypeId) === CUSTOM_RANGE)

  const isAllCurrentIntervalsSelected = computed(() => toValue(intervalTypeId) === ALL_CURRENT)

  // "All current intervals" and a custom range go without interval ids:
  // the server takes the intervals current or future at the generation
  const intervalIds = computed(() => {
    if (isAllCurrentIntervalsSelected.value || isCustomRangeSelected.value) {
      return null
    }

    return toValue(selectedIntervalIds)
  })

  const readQuery = computed(() => {
    return {
      ...selection.value,
      workspaceIds: toValue(workspaceIds),
      intervalIds: intervalIds.value,
      statusIds: toValue(gradeTypes)
    }
  })

  const isWorkspaceFilterNarrowing = computed(() => {
    return !includesAll({
      selectedValues: toValue(workspaceIds),
      allValues: toValue(allWorkspaceIds)
    })
  })

  const isStatusFilterNarrowing = computed(() => {
    return !includesAll({ selectedValues: toValue(gradeTypes), allValues: ALL_GRADE_TYPES })
  })

  const isAlignmentNarrowed = computed(() => {
    return (
      isWorkspaceFilterNarrowing.value ||
      isStatusFilterNarrowing.value ||
      !isAllCurrentIntervalsSelected.value
    )
  })

  // the Interval and Status filters leave the Engagement report unchanged (FR-3.10)
  const isEngagementNarrowed = computed(() => isWorkspaceFilterNarrowing.value)

  return {
    selection,
    readQuery,
    isCustomRangeSelected,
    isAlignmentNarrowed,
    isEngagementNarrowed
  }
}

/*
 * The names of the Home filters that apply to a kept Alignment report, for the line of FR-0.27:
 * the label key of the Interval filter option and the name of the Workspace filter when it
 * holds one Workspace, null when it holds several (the filter shows "Workspaces" then).
 */
export const getAiReportFilterNames = ({
  intervalTypeId = null,
  workspaceIds = [],
  workspaces = []
} = {}) => {
  const normalizedWorkspaceIds = workspaceIds.map(Number)

  const selectedWorkspaces = workspaces.filter(workspace => {
    return normalizedWorkspaceIds.includes(Number(workspace[WORKSPACE_ENTITY_KEYS.ID]))
  })

  return {
    intervalLabelKey: AI_REPORT_INTERVAL_FILTER_LABEL_KEYS[intervalTypeId] ?? null,
    workspaceName:
      selectedWorkspaces.length === 1 ? selectedWorkspaces[0][WORKSPACE_ENTITY_KEYS.NAME] : null
  }
}

const isElementShown = element => {
  return !isNil(element) && !element[AI_REPORT_ELEMENT_KEYS.DELETED]
}

/*
 * The served pairs, each once, without the pairs of a deleted Element (FR-3.3), cut to the
 * weakest ones when more than the limit match the Home filters (FR-1.23, FR-3.6, FR-3.7).
 * A row without a parent is tested by its Element alone (FR-1.15).
 */
export const getAlignmentReportPairs = (report = {}) => {
  const elements = report[AI_REPORT_KEYS.ELEMENTS] ?? {}

  const servedPairs = uniqBy(report[AI_REPORT_KEYS.PAIRS] ?? [], AI_REPORT_PAIR_KEYS.PAIR_ID)

  const matchingPairs = servedPairs.filter(pair => {
    const parentId = pair[AI_REPORT_PAIR_KEYS.PARENT_ID]
    const isParentShown = isNil(parentId) || isElementShown(elements[parentId])

    return isParentShown && isElementShown(elements[pair[AI_REPORT_PAIR_KEYS.CHILD_ID]])
  })

  if (matchingPairs.length <= AI_REPORT_PAIRS_LIMIT) {
    return { pairs: matchingPairs, hiddenPairsCount: 0 }
  }

  // toSorted is stable, so pairs with the same score keep the served order
  const weakestPairIds = matchingPairs
    .toSorted((a, b) => {
      return a[AI_REPORT_PAIR_KEYS.ALIGNMENT_SCORE] - b[AI_REPORT_PAIR_KEYS.ALIGNMENT_SCORE]
    })
    .slice(0, AI_REPORT_WEAKEST_PAIRS_COUNT)
    .map(pair => pair[AI_REPORT_PAIR_KEYS.PAIR_ID])

  return {
    pairs: matchingPairs.filter(pair => {
      return weakestPairIds.includes(pair[AI_REPORT_PAIR_KEYS.PAIR_ID])
    }),
    hiddenPairsCount: matchingPairs.length - AI_REPORT_WEAKEST_PAIRS_COUNT
  }
}

/*
 * The served sections with the given pairs in the served order of the pairs; a pair stands
 * under each section that lists it (FR-1.23), a section without pairs is left out.
 */
export const getAlignmentReportSections = ({ report = {}, pairs = [] } = {}) => {
  return (report[AI_REPORT_KEYS.SECTIONS] ?? [])
    .map(section => {
      const sectionPairIds = section[AI_REPORT_SECTION_KEYS.PAIR_IDS] ?? []

      return {
        [AI_REPORT_SECTION_KEYS.GROUP]: section[AI_REPORT_SECTION_KEYS.GROUP] ?? null,
        [AI_REPORT_KEYS.PAIRS]: pairs.filter(pair => {
          return sectionPairIds.includes(pair[AI_REPORT_PAIR_KEYS.PAIR_ID])
        })
      }
    })
    .filter(section => !isEmpty(section[AI_REPORT_KEYS.PAIRS]))
}

// "No pairs match the filters." (FR-3.14)
export const isAlignmentReportEmpty = (report = {}) => {
  return isEmpty(getAlignmentReportPairs(report).pairs)
}

// "No assigned owners found" (FR-2.3, FR-3.15)
export const isEngagementReportEmpty = (report = {}) => {
  return isEmpty(report[AI_REPORT_KEYS.ROWS])
}
